import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Stack, Title, Text, Button, Paper, Group, Alert, Collapse, Loader, Pagination, Box, Tooltip, Skeleton, Grid, Card, ThemeIcon } from '@mantine/core';
import { IconAlertCircle, IconHistory, IconCalendarPlus } from '@tabler/icons-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useAppUser } from '../../hooks/useAppUser';
import { useAuth } from '../../hooks/useAuth';
import { useFeatureFlags, useFlagEnabledAt } from '../../hooks/useFeatureFlags';
import { usePostHog } from '@posthog/react';
import { AnalyticsEvent } from '../../utils/analytics';
import { FeatureAnnouncementModal } from '../Announcements/FeatureAnnouncementModal';
import { pickFeatureAnnouncement } from '../../utils/featureAnnouncements';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useMealPlanning } from '../../hooks/useMealPlanning';
import { useSubscription } from '../../hooks/useSubscription';
import { usePlanningLimits } from '../../hooks/usePlanningLimits';
import { useRecipes } from '../../hooks/useRecipes';
import { usePantry } from '../../hooks/usePantry';
import { SubscriptionStatusBadge } from '../UI/SubscriptionStatusBadge';
import { MobileOptimized } from '../Layout/MobileOptimized';
import { LoadingScreen } from '../Layout/LoadingScreen';
import { FailedMealPlansModal } from '../MealPlan/FailedMealPlansModal';
import { MealPlanCardMenu } from '../MealPlan/MealPlanCardMenu';
import { ImportRecipeModal } from '../Recipe/RecipeImport';
import { ActiveMealPlanCard } from './ActiveMealPlanCard';
import { NoActiveMealPlanCTA } from './NoActiveMealPlanCTA';
import { UpcomingMealPlanCard } from './UpcomingMealPlanCard';
import { PlanGenerationProgress } from './PlanGenerationProgress';
import { EmptyCookbookCTA } from './EmptyCookbookCTA';
import { PopulatedCookbookCTA } from './PopulatedCookbookCTA';
import { EmptyPantryCTA } from './EmptyPantryCTA';
import { MealPlan, MealPlanGenerationProgress, Recipe } from '../../types/mealPlan';
import { getStatusBadge, formatCreatedDate, sortMealPlansByStartDate, formatRecipeSource, formatPlanMealsSummary, formatDateRange, isRecipeScaled, getExpectedDays } from '../../utils/mealPlanUtils';
import { AssignmentThumbnail, assignmentThumbnailLabel, hasAssignmentThumbnail, stripAssignments } from '../MealPlanning/AssignmentThumbnail';
import { showMealPlanDeletedNotification } from '../../utils/mealPlanNotifications';
import { freshestProgress, withFreshestGenerationProgress } from '../../utils/progressFreshness';
import styles from './MainDashboard.module.css';
import { buildRecipePath, buildPlanPath } from '../../utils/routeHelpers';
import { TasteTuner } from '../TasteTuner/TasteTuner';
import { ContactEmailNudge } from './ContactEmailNudge';
import { collection, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useFirestoreTrigger, FirestoreTriggerSnapshot } from '../../hooks/useFirestoreTrigger';
import { getPageCache, setPageCache, subscribePageCache } from '../../utils/pageCache';
import { nextMealPlansFetch, isLatestMealPlansFetch } from '../../utils/mealPlansFetchGuard';
import { restoreMealPlanInCache } from '../../utils/mealPlansCache';


const DASHBOARD_PAGE_SIZE = 5;

// Everything the dashboard shows about meal plans, derived from the full plan
// list. Pure so it can both process a fresh fetch and seed initial state from
// the session cache (rendering the last-known dashboard on a back navigation
// without a skeleton pass).
const derivePlanState = (plans: MealPlan[]) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().split('T')[0];

  // Current meal plans (end date >= today OR in-flight OR failed), excluding
  // active plans that cover today's date (shown in ActiveMealPlanCard)
  const currentPlans = plans.filter(plan => {
    const isInFlight = ['pending', 'generating'].includes(plan.generationStatus.status);
    const isFailed = plan.generationStatus.status === 'failed';
    const isCurrentOrFuture = plan.endDate >= todayStr;

    // Check if this plan covers today's date
    const [startYear, startMonth, startDay] = plan.startDate.split('-').map(Number);
    const [endYear, endMonth, endDay] = plan.endDate.split('-').map(Number);
    const startDate = new Date(startYear, startMonth - 1, startDay);
    const endDate = new Date(endYear, endMonth - 1, endDay);
    const coversToday = today >= startDate && today <= endDate && plan.generationStatus.status === 'completed';

    // Include if it meets the criteria but doesn't cover today
    return (isInFlight || isFailed || isCurrentOrFuture) && !coversToday;
  });

  // Sort by start date (soonest first)
  const sortedCurrentPlans = sortMealPlansByStartDate(currentPlans);

  // Find meal plan that covers today (active or inactive)
  const activeMealPlan = plans.find(plan => {
    if (plan.generationStatus.status !== 'completed') {
      return false;
    }

    // Parse dates manually to avoid timezone issues
    const [startYear, startMonth, startDay] = plan.startDate.split('-').map(Number);
    const [endYear, endMonth, endDay] = plan.endDate.split('-').map(Number);
    const startDate = new Date(startYear, startMonth - 1, startDay);
    const endDate = new Date(endYear, endMonth - 1, endDay);

    // Check if today falls within the meal plan date range
    return today >= startDate && today <= endDate;
  }) || null;

  // Check if there are any meal plans (any status except failed) covering the next 7 days
  const next7Days = new Date();
  next7Days.setDate(next7Days.getDate() + 7);
  next7Days.setHours(0, 0, 0, 0);

  const hasUpcomingMealPlan = plans.some(plan => {
    // Consider any plan that's not failed
    if (plan.generationStatus.status === 'failed') {
      return false;
    }

    // Parse dates manually to avoid timezone issues
    const [startYear, startMonth, startDay] = plan.startDate.split('-').map(Number);
    const [endYear, endMonth, endDay] = plan.endDate.split('-').map(Number);
    const startDate = new Date(startYear, startMonth - 1, startDay);
    const endDate = new Date(endYear, endMonth - 1, endDay);

    // Check if any part of the meal plan falls within the next 7 days
    return (startDate >= today && startDate <= next7Days) ||
           (endDate >= today && endDate <= next7Days) ||
           (startDate <= today && endDate >= next7Days);
  });

  // Count generation statuses and collect failed plans
  const generationCounts = {
    pending: 0,
    generating: 0,
    failed: 0,
  };
  const failedMealPlans: MealPlan[] = [];

  plans.forEach((plan) => {
    if (plan.generationStatus.status === 'pending') {
      generationCounts.pending++;
    } else if (plan.generationStatus.status === 'generating') {
      generationCounts.generating++;
    } else if (plan.generationStatus.status === 'failed') {
      generationCounts.failed++;
      failedMealPlans.push(plan);
    }
  });

  return {
    currentMealPlans: sortedCurrentPlans,
    activeMealPlan,
    hasUpcomingMealPlan,
    generationCounts,
    failedMealPlans,
  };
};

export const MainDashboard = () => {
  usePageTitle('Dashboard | Meals You Love');
  const { user } = useAppUser();
  const { getUserMealPlans, deleteMealPlan, activateMealPlan, error } = useMealPlanning();
  const { subscription, hasAccess } = useSubscription();
  const { isStartPlanningDisabled, startPlanningTooltipText } = usePlanningLimits();
  const { cookbookCount, sampleRecipes, loading: recipesLoading } = useRecipes();
  const { pantryCount, loading: pantryLoading } = usePantry();
  const navigate = useNavigate();
  const location = useLocation();

  // Seed from the plans this session already fetched (here or on the meal
  // plan history page — both cache the same list) so a back navigation
  // renders the dashboard on the first paint; the mount fetch then refreshes
  // everything silently.
  const plansCacheKey = user ? `meal-plans:${user.uid}` : null;
  const seededPlans = getPageCache<MealPlan[]>(plansCacheKey);
  const seededState = seededPlans ? derivePlanState(seededPlans) : null;

  // One-time feature announcement: the first registry entry this user is
  // eligible for and hasn't seen. The local dismissed set bridges the beat
  // between recording seen and the profile refetch carrying it back.
  const { user: authUser } = useAuth();
  const posthog = usePostHog();
  const { data: featureFlags } = useFeatureFlags();
  const { data: flagEnabledAt } = useFlagEnabledAt();
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState<Set<string>>(new Set());
  const announcement = user?.completedOnboarding
    ? pickFeatureAnnouncement(
        featureFlags,
        flagEnabledAt,
        user?.seenAnnouncements,
        dismissedAnnouncements,
        user?.createdAt
      )
    : undefined;

  const announcementShownRef = useRef<string | null>(null);
  useEffect(() => {
    if (announcement && announcementShownRef.current !== announcement.id) {
      announcementShownRef.current = announcement.id;
      posthog?.capture(AnalyticsEvent.FeatureAnnouncementShown, {
        announcement_id: announcement.id,
      });
    }
  }, [announcement, posthog]);

  const closeAnnouncement = (outcome: 'cta' | 'dismissed') => {
    if (!announcement) return;
    posthog?.capture(
      outcome === 'cta'
        ? AnalyticsEvent.FeatureAnnouncementCtaClicked
        : AnalyticsEvent.FeatureAnnouncementDismissed,
      { announcement_id: announcement.id }
    );
    setDismissedAnnouncements(prev => new Set(prev).add(announcement.id));
    // Fire-and-forget: a failed write re-shows next visit, which is harmless
    (async () => {
      try {
        const token = await authUser?.getIdToken();
        if (!token) return;
        await fetch(`${process.env.REACT_APP_API_URL}/api/user/announcements/seen`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ announcementId: announcement.id }),
        });
      } catch (error) {
        console.error('Failed to record announcement as seen:', error);
      }
    })();
    if (outcome === 'cta') {
      navigate(announcement.ctaPath);
    }
  };

  const [currentMealPlans, setCurrentMealPlans] = useState<MealPlan[]>(
    seededState?.currentMealPlans ?? []
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [generationCounts, setGenerationCounts] = useState<{
    pending: number;
    generating: number;
    failed: number;
  }>(seededState?.generationCounts ?? { pending: 0, generating: 0, failed: 0 });
  const [failedMealPlansModalOpened, setFailedMealPlansModalOpened] = useState(false);
  const [failedMealPlans, setFailedMealPlans] = useState<MealPlan[]>(
    seededState?.failedMealPlans ?? []
  );

  const [activeMealPlan, setActiveMealPlan] = useState<MealPlan | null>(
    seededState?.activeMealPlan ?? null
  );
  const [deletedMealPlanIds, setDeletedMealPlanIds] = useState<Set<string>>(new Set());
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(!!seededState);
  const [hasUpcomingMealPlan, setHasUpcomingMealPlan] = useState(
    seededState?.hasUpcomingMealPlan ?? false
  );
  const [tasteTunerVisible, setTasteTunerVisible] = useState(
    !!(location.state as any)?.showTasteTuner && sessionStorage.getItem('tasteTunerCompleted') !== 'true'
  );
  const [planReady, setPlanReady] = useState(false);
  const hadActiveGenerationsRef = useRef(false);
  const tasteTunerClosedRef = useRef(sessionStorage.getItem('tasteTunerCompleted') === 'true');
  const [importModalOpened, setImportModalOpened] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('pinterest') === 'connected';
  });
  // Family size derives from the merged app user's profile — session-cached
  // on remounts, refreshed by useAppUser — instead of a bespoke profile fetch.
  const familySize = useMemo(
    () =>
      user?.familyProfile
        ? {
            adults: user.familyProfile.adults || 2,
            kids: user.familyProfile.kids || 0,
          }
        : null,
    [user?.familyProfile]
  );

  // Freshest progress seen per in-flight plan, so a REST refetch that
  // resolves after newer listener progress cannot rewind the bar (the
  // backwards-flicker bug). Rebuilt each snapshot, so finished plans age out.
  const liveProgressRef = useRef<
    Map<string, MealPlanGenerationProgress | undefined>
  >(new Map());

  // Keyed on the uid, not the merged user object: useAppUser's user takes a
  // new identity every time profile/subscription state settles during
  // startup, and each identity re-fired the mount effect below — a cold
  // dashboard open fetched the plan list 6-7 times. The uid is the only part
  // of the user this fetch actually depends on.
  const uid = user?.uid;
  const fetchMealPlans = useCallback(async (showLoading: boolean = true) => {
    if (!uid) return;

    const fetchToken = nextMealPlansFetch();
    try {
      const plans = await getUserMealPlans();
      // A newer list fetch started while this one was in flight (e.g. an
      // Undo's refetch racing the post-delete refetch) — this response is
      // stale and must touch neither state nor the cache.
      if (!isLatestMealPlansFetch(fetchToken)) return;
      setPageCache(`meal-plans:${uid}`, plans);

      const derived = derivePlanState(plans);

      // This fetch read the plan docs when it started; the listener may have
      // delivered newer progress since. The freshest copy wins, so a slow
      // response cannot move a generating plan's bar backwards.
      setCurrentMealPlans(
        withFreshestGenerationProgress(
          derived.currentMealPlans,
          liveProgressRef.current
        )
      );

      // Reset to first page when meal plans change
      setCurrentPage(1);

      setActiveMealPlan(derived.activeMealPlan);
      setHasUpcomingMealPlan(derived.hasUpcomingMealPlan);
      setGenerationCounts(derived.generationCounts);
      setFailedMealPlans(derived.failedMealPlans);

      // Mark as initially loaded after first successful fetch. Unconditional
      // (React bails on same-value sets): reading hasInitiallyLoaded here put
      // it in this callback's deps, so the first success minted a new
      // callback identity and re-fired the mount effect — one extra fetch
      // per load.
      setHasInitiallyLoaded(true);
    } catch (error) {
      console.error('Error fetching meal plans:', error);
    }
  }, [uid, getUserMealPlans]);

  useEffect(() => {
    if (uid && hasAccess) {
      fetchMealPlans();
    }
  }, [uid, hasAccess, fetchMealPlans]);

  // Track plan-list writes made by other surfaces while this dashboard is
  // mounted — e.g. a delete's Undo tapped after navigating here, whose
  // refetch runs in the deleting page's (unmounted) closure. Whoever fetches
  // writes the shared cache; this keeps the visible dashboard in sync with it.
  useEffect(() => {
    return subscribePageCache<MealPlan[]>(plansCacheKey, (plans) => {
      const derived = derivePlanState(plans);
      setCurrentMealPlans(
        withFreshestGenerationProgress(derived.currentMealPlans, liveProgressRef.current)
      );
      setActiveMealPlan(derived.activeMealPlan);
      setHasUpcomingMealPlan(derived.hasUpcomingMealPlan);
      setGenerationCounts(derived.generationCounts);
      setFailedMealPlans(derived.failedMealPlans);
      setHasInitiallyLoaded(true);
    });
  }, [plansCacheKey]);

  // Check subscription access and redirect if needed
  useEffect(() => {
    if (subscription && !hasAccess) {
      navigate('/billing');
    }
  }, [subscription, hasAccess, navigate]);


  const handleStartPlanning = () => {
    navigate('/planning');
  };

  const handleViewPlan = (plan: MealPlan) => {
    if (!user) return;
    navigate(buildPlanPath(plan, user.uid));
  };

  const handleRecipeClick = (recipe: Recipe, mealPlanId?: string, assignmentId?: string) => {
    if (!user) return;
    navigate(buildRecipePath(recipe, user.uid), {
      state: {
        // Only pass recipe state if it differs from canonical recipe
        recipe: isRecipeScaled(recipe) ? recipe : undefined,
        ...(mealPlanId && assignmentId && { mealPlanId, assignmentId })
      }
    });
  };

  const handleCookRecipe = (recipe: Recipe) => {
    if (!user) return;
    navigate(`${buildRecipePath(recipe, user.uid)}/cook`, { state: { recipe } });
  };

  const handleViewFailedMealPlans = useCallback(() => {
    setFailedMealPlansModalOpened(true);
  }, []);

  const handleDeleteMealPlan = useCallback(async (mealPlan: MealPlan) => {
    const planId = mealPlan.id;
    const success = await deleteMealPlan(planId);
    if (success) {
      // Mark as deleted for UI feedback
      setDeletedMealPlanIds(prev => new Set(prev).add(planId));

      showMealPlanDeletedNotification({
        mealPlan,
        getIdToken: async () => user?.getIdToken(),
        onRestored: async () => {
          setDeletedMealPlanIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(planId);
            return newSet;
          });
          // Put the plan straight back (we hold the full object) so
          // reappearing doesn't depend on the refetch below — the cache
          // write notifies this dashboard's subscription synchronously.
          if (user?.uid) {
            restoreMealPlanInCache(user.uid, mealPlan);
          }
          await fetchMealPlans(false);
        },
      });

      // Refresh the meal plans list
      await fetchMealPlans(false);
    } else {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete meal plan. Please try again.',
        color: 'red',
      });
    }
  }, [deleteMealPlan, fetchMealPlans, user]);

  // A failed plan's Clear button is a delete, so it takes a couple of seconds
  // to land; the button spins for its own plan until the card is gone.
  const [clearingPlanId, setClearingPlanId] = useState<string | null>(null);

  const handleClearFailedPlan = useCallback(async (plan: MealPlan) => {
    setClearingPlanId(plan.id);
    try {
      await handleDeleteMealPlan(plan);
    } finally {
      setClearingPlanId(null);
    }
  }, [handleDeleteMealPlan]);

  // The generation banners' Collapses animate closed after the counts reach
  // 0; keep the last non-zero totals so the exiting banners don't flash
  // "0 meal plans are being created" / "0 meal plans failed".
  const lastGenerationTotalRef = useRef(1);
  const lastFailedCountRef = useRef(1);
  useEffect(() => {
    const total = generationCounts.pending + generationCounts.generating;
    if (total > 0) {
      lastGenerationTotalRef.current = total;
    }
    if (generationCounts.failed > 0) {
      lastFailedCountRef.current = generationCounts.failed;
    }
  }, [generationCounts]);
  const displayFailedCount = generationCounts.failed || lastFailedCountRef.current;

  // Track generation activity for the taste tuner / plan-ready transitions
  useEffect(() => {
    const hasActiveGenerations = generationCounts.pending > 0 || generationCounts.generating > 0;

    if (hasActiveGenerations) {
      hadActiveGenerationsRef.current = true;
      if (!tasteTunerClosedRef.current) {
        setTasteTunerVisible(true);
      }
    } else if (hadActiveGenerationsRef.current) {
      // Generations just finished
      setPlanReady(true);
      hadActiveGenerationsRef.current = false;
    }
  }, [generationCounts]);

  // Realtime refresh while there are active generations: watch the user's
  // in-flight meal plan docs and refetch the list whenever one changes or
  // reaches a terminal status (i.e. leaves the query).
  const inFlightPlansQuery = useMemo(
    () =>
      user?.uid
        ? query(
            collection(db, 'users', user.uid, 'meal_plans'),
            where('generationStatus.status', 'in', ['pending', 'generating'])
          )
        : null,
    [user?.uid]
  );
  // Statuses as of the last snapshot, so progress ticks can be told apart from
  // a plan actually entering, leaving, or changing status.
  const inFlightStatusesRef = useRef<Map<string, string>>(new Map());

  const handleInFlightChange = useCallback(
    (snapshot: FirestoreTriggerSnapshot) => {
      // Polling fallback ticks carry no snapshot, so they refetch as before.
      if (!snapshot || !('docs' in snapshot)) {
        fetchMealPlans(false);
        return;
      }

      const statuses = new Map<string, string>();
      const progressById = new Map<
        string,
        MealPlanGenerationProgress | undefined
      >();
      snapshot.docs.forEach((doc) => {
        const data = doc.data() as Partial<MealPlan>;
        statuses.set(doc.id, data.generationStatus?.status ?? '');
        // Guarded against the snapshot itself being stale: a fresh subscribe
        // can replay a cached snapshot older than what is already on screen.
        progressById.set(
          doc.id,
          freshestProgress(
            liveProgressRef.current.get(doc.id),
            data.generationProgress
          )
        );
      });
      liveProgressRef.current = progressById;

      const previous = inFlightStatusesRef.current;
      // An empty snapshot always refetches: the work may have finished between
      // the last fetch and this subscription, and nothing else would notice.
      const structuralChange =
        statuses.size === 0 ||
        statuses.size !== previous.size ||
        Array.from(statuses).some(([id, status]) => previous.get(id) !== status);
      inFlightStatusesRef.current = statuses;

      // Progress arrives every few seconds. Taking it straight off the snapshot
      // keeps the card moving without a full plan refetch per meal.
      setCurrentMealPlans((plans) =>
        plans.map((plan) =>
          progressById.has(plan.id)
            ? { ...plan, generationProgress: progressById.get(plan.id) }
            : plan
        )
      );

      if (structuralChange) {
        fetchMealPlans(false); // false = no loading spinner
      }
    },
    [fetchMealPlans]
  );

  useFirestoreTrigger({
    enabled: generationCounts.pending > 0 || generationCounts.generating > 0,
    target: inFlightPlansQuery,
    onTrigger: handleInFlightChange,
  });

  // No cleanup needed for the new approach

  // Filter out deleted meal plans, then paginate
  const visibleMealPlans = currentMealPlans.filter(plan => !deletedMealPlanIds.has(plan.id));
  const startIndex = (currentPage - 1) * DASHBOARD_PAGE_SIZE;
  const paginatedMealPlans = visibleMealPlans.slice(startIndex, startIndex + DASHBOARD_PAGE_SIZE);
  const totalPages = Math.ceil(visibleMealPlans.length / DASHBOARD_PAGE_SIZE);

  // Get time-aware greeting
  const getTimeAwareGreeting = (firstName?: string) => {
    const hour = new Date().getHours();
    let greeting;
    
    if (hour < 12) {
      greeting = 'Good morning';
    } else if (hour < 17) {
      greeting = 'Good afternoon';
    } else {
      greeting = 'Good evening';
    }
    
    return firstName ? `${greeting}, ${firstName}` : `${greeting}`;
  };

  

  // Wait for family data and then set subtitle once. The pick is kept for the
  // whole session so a back navigation doesn't roll a new greeting (or flash
  // the greeting skeleton while re-rolling one).
  const [randomSubtitle, setRandomSubtitle] = useState<string>(
    getPageCache<string>(user ? `dashboard-subtitle:${user.uid}` : null) ?? ""
  );

  useEffect(() => {
    const COMMON_SUBTITLES = [
      "Ready to plan some delicious meals?",
      "Let's get cooking!",
      "What's cookin'?",
      "What's on the menu?",
      "What's on the menu today?",
      "Time to spice up your week!",
      "Your next favorite meal awaits",
      "Let's make mealtime magical",
      "Ready to discover new flavors?",
      "Cooking adventures start here",
      "Your culinary journey begins now",
      "Your next culinary adventure awaits",
      "Fresh ideas for fresh meals",
      "Let's make dinner exciting",
      "Discover your next favorite dish",
      "Let's find meals you'll absolutely love",
      "Ready to fall in love with dinner again?",
      "Let's explore some new flavors",
      "No leftovers. Unless you want them.",
      "Discover a new favorite dish, or three",
      "Dinner plans?",
      "Because takeout can wait",
      "Good taste starts here",
      "Let's make something amazing tonight",
      "Great meals, no stress",
      "Let's make mealtime easy and delicious",
      "Small steps to big flavor",
      "Good food, good mood",
      "Because you deserve something tasty",
      "Let's get your taste buds excited",
      "Cooking inspiration, served fresh",
      "Delicious starts here",
      "One recipe away from a great day",
      "Let's make dinner a little easier tonight",
    ];

    const SOLO_SUBTITLES = [
      "Cook for one, but make it unforgettable", 
      "Fuel your week with flavors you love",
      "Meals made just for you",
      "Discover your next comfort food",
      "Less stress, more flavor",
      "Your kitchen, your rules",
      "Tonight's chef? You.",
      "Meals tailored just for you",
      "Let's get you fed",
      "Who needs a sous-chef anyway?",
      "Your taste buds are the VIPs here",
      "It's just you, me, and the leftovers",
      "Chef's hat optional",
      "Easy meals, big flavor",
      "Let's make dinner effortless",
      "Your next crave-worthy meal awaits",
      "Simple meals, delicious results",
      "Let's make something tasty together",
      "Your dinner inspiration starts here",
      "Delicious made doable",
    ];

    const FAMILY_SUBTITLES = [
      "Creating meals your family will love",
      "Time to create your family's favorites",
      "Meals that bring everyone to the table",
      "Let's cook up some family favorites",
      "Let's cook up a new family favorite",
      "Something tasty for everyone",
      "Let's get everyone fed",
      "Let's make mealtime a little easier",
      "Meals so good, they'll fight for seconds",
      "What's the family in the mood for tonight?",
      "Let's make dinner something to look forward to",
      "Your family's next favorite meal starts here",
      "Because everyone deserves seconds",
      "What's for di— We'll take it from here.",
      "Good food brings everyone together",
      "Let's make every night a family favorite",
      "Let's make dinner easy for everyone",
      "Let's find flavors that bring everyone to the table",
    ];

    if (familySize && !randomSubtitle) {
      const totalFamilySize = familySize.adults + familySize.kids;
      const isFamily = totalFamilySize > 1;
      const subtitles = isFamily ? FAMILY_SUBTITLES : SOLO_SUBTITLES;
      subtitles.push(...COMMON_SUBTITLES);
      const randomIndex = Math.floor(Math.random() * subtitles.length);
      setRandomSubtitle(subtitles[randomIndex]);
      if (user) {
        setPageCache(`dashboard-subtitle:${user.uid}`, subtitles[randomIndex]);
      }
    }
  }, [familySize, randomSubtitle, user]);


  // Redirect if no access (will be handled by useEffect, but prevent rendering)
  if (subscription && !hasAccess) {
    return <LoadingScreen />;
  }

  // Main dashboard view
  return (
    <MobileOptimized>
      {announcement && (
        <FeatureAnnouncementModal
          announcement={announcement}
          opened
          onCta={() => closeAnnouncement('cta')}
          onDismiss={() => closeAnnouncement('dismissed')}
          // A maxed-out user would dead-end in the wizard; same guard and
          // copy as the nav's Start planning button
          ctaDisabledReason={
            announcement.ctaPath === '/planning' && isStartPlanningDisabled
              ? startPlanningTooltipText
              : undefined
          }
        />
      )}
      <Stack gap="lg">
        <Stack gap="sm">
          {!hasInitiallyLoaded || !randomSubtitle ? (
            <>
              {/* min() caps: fixed 280/300px run past the right edge on
                  ~320px screens */}
              <Skeleton height={32} width="min(280px, 70%)" mb={4} />
              <Skeleton height={25} width="min(300px, 85%)" />
            </>
          ) : (
            <>
              <Title order={2}>
                {getTimeAwareGreeting(user?.displayName?.split(' ')[0])}
              </Title>
              <Text c="dimmed">
                {randomSubtitle}
              </Text>
            </>
          )}
        </Stack>

        <SubscriptionStatusBadge variant="alert" showUpgradeButton />

        <Collapse in={!!error}>
          <Alert icon={<IconAlertCircle size={16} color="red" />} color="red" radius="lg">
            <Text size="sm" c="red.7">{error}</Text>
          </Alert>
        </Collapse>

        {/* Active generation: live progress for a single plan, since the meals
            being named are what makes the wait bearable. Several at once fall
            back to a count, as the names would be ambiguous. */}
        <Collapse in={generationCounts.pending > 0 || generationCounts.generating > 0}>
          <Alert icon={<Loader size={16} color="blue" />} color="blue" variant="light" radius="lg">
            {(() => {
              const total =
                generationCounts.pending + generationCounts.generating ||
                lastGenerationTotalRef.current;
              const inFlight = visibleMealPlans.filter(plan =>
                ['pending', 'generating'].includes(plan.generationStatus.status)
              );

              if (total !== 1 || inFlight.length !== 1) {
                return (
                  <Text size="sm" c="blue.7">
                    {total} meal plans are being created...
                  </Text>
                );
              }

              return (
                <PlanGenerationProgress
                  variant="banner"
                  progress={inFlight[0].generationProgress}
                  startedAt={inFlight[0].createdAt}
                  household={inFlight[0].profileSnapshot}
                  expectedDays={getExpectedDays(inFlight[0]).map((d) => d.dayName)}
                  additionalMeals={inFlight[0].preferences.additionalMeals}
                />
              );
            })()}
          </Alert>
        </Collapse>

        <ContactEmailNudge />

        {/* Taste Tuner - persists independently of generation status */}
        {tasteTunerVisible && (user?.familyProfile || (location.state as any)?.showTasteTuner) && !user?.tasteTunerDismissed && (
          <TasteTuner
            planReady={planReady}
            onComplete={() => { tasteTunerClosedRef.current = true; setTasteTunerVisible(false); sessionStorage.setItem('tasteTunerCompleted', 'true'); }}
            onViewPlan={() => {
              tasteTunerClosedRef.current = true;
              setTasteTunerVisible(false);
              const completedPlan = [...currentMealPlans]
                .filter(p => p.generationStatus.status === 'completed')
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
              if (completedPlan && user) {
                navigate(buildPlanPath(completedPlan, user.uid));
              }
            }}
            onDismiss={async () => {
              tasteTunerClosedRef.current = true;
              setTasteTunerVisible(false);
              try {
                const token = await user?.getIdToken?.();
                if (token) {
                  await fetch(
                    `${process.env.REACT_APP_API_URL}/api/user/taste-tuner-dismissed`,
                    {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${token}` },
                    }
                  );
                }
              } catch (error) {
                console.error('Failed to dismiss taste tuner:', error);
              }
            }}
          />
        )}

        {/* Failed Generation Notification */}
        <Collapse in={generationCounts.failed > 0}>
          <Alert color="red" variant="light" radius="lg">
            <Group gap="sm" align="center" style={{ minHeight: 20 }}>
              <IconAlertCircle size={16} color="var(--mantine-color-red-6)" />
              <Text size="sm" c="red.7" style={{ flex: 1 }}>
                {displayFailedCount} meal plan{displayFailedCount !== 1 ? 's' : ''} failed
              </Text>
              <Button
                size="xs"
                variant="subtle"
                color="red"
                onClick={handleViewFailedMealPlans}
              >
                View Details
              </Button>
            </Group>
          </Alert>
        </Collapse>

        {/* Active Meal Plan or Call to Action */}
        {!hasInitiallyLoaded ? (
          <>
            <Skeleton h={{ base: 480, sm: 393 }} radius="xl" />
            <Stack gap="sm">
              <Skeleton height={36} width={220} mb={7} />
              {Array.from({ length: 2 }).map((_, i) => (
                <Paper key={i} p="md" radius="lg" withBorder>
                  {/* Mobile skeleton */}
                  <Box hiddenFrom="sm">
                    <Stack gap="sm">
                      <Group gap="sm">
                        <Skeleton height={20} width="50%" />
                        <Skeleton height={20} width={60} radius="xl" />
                      </Group>
                      <Skeleton height={14} width="80%" />
                      <Group gap="xs">
                        <Skeleton w={80} h={80} radius="md" />
                        <Skeleton w={80} h={80} radius="md" />
                        <Skeleton w={80} h={80} radius="md" />
                      </Group>
                      <Skeleton height={36} width="100%" radius="md" />
                    </Stack>
                  </Box>
                  {/* Desktop skeleton */}
                  <Box visibleFrom="sm">
                    <Group align="flex-start" gap="lg">
                      <Group gap="xs">
                        {Array.from({ length: 5 }).map((_, j) => (
                          <Skeleton key={j} w={80} h={80} radius="sm" />
                        ))}
                      </Group>
                      <Stack gap="xs" style={{ flex: 1 }}>
                        <Group justify="space-between">
                          <Stack gap={4}>
                            <Group gap="sm">
                              <Skeleton height={20} width={180} />
                              <Skeleton height={20} width={60} radius="xl" />
                            </Group>
                            <Skeleton height={14} width={250} />
                          </Stack>
                          <Skeleton height={36} width={60} radius="md" />
                        </Group>
                      </Stack>
                    </Group>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </>
        ) : (
          activeMealPlan ? (
            <ActiveMealPlanCard
              mealPlan={activeMealPlan}
              userId={user?.uid || ''}
              onViewPlan={handleViewPlan}
              onRecipeClick={handleRecipeClick}
              onCookRecipe={handleCookRecipe}
              onDeletePlan={handleDeleteMealPlan}
              onPlanUpdated={() => fetchMealPlans(false)}
              profilePublic={user?.profilePublic}
              calendarConnected={user?.familyProfile?.googleCalendarConnected ?? false}
              onActivatePlan={async (planId: string) => {
                const success = await activateMealPlan(planId);
                if (success) {
                  handleViewPlan(activeMealPlan);
                }
              }}
            />
          ) : !hasUpcomingMealPlan ? (
            <NoActiveMealPlanCTA
              onStartPlanning={handleStartPlanning}
              isStartPlanningDisabled={isStartPlanningDisabled}
              startPlanningTooltipText={startPlanningTooltipText}
              familySize={familySize || undefined}
            />
          ) : isStartPlanningDisabled ? (
            <UpcomingMealPlanCard
              onViewHistory={() => navigate('/meal-plans?filter=upcoming')}
            />
          ) : (
            <Grid gutter="md">
              <Grid.Col span={{ base: 12, md: 8 }}>
                <UpcomingMealPlanCard
                  onViewHistory={() => navigate('/meal-plans?filter=upcoming')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Card 
                  radius="lg"
                  p="lg"
                  style={{ 
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    background: 'white',
                    border: '1px solid var(--mantine-color-gray-3)'
                  }}
                  onClick={handleStartPlanning}
                >
                  <Group gap="md" align="center">
                    <ThemeIcon 
                      size={40} 
                      radius="md" 
                      variant="light" 
                      color="blue"
                    >
                      <IconCalendarPlus size={20} />
                    </ThemeIcon>
                    <Stack gap={4}>
                      <Text fw={600} size="md" c="dark">
                        Plan More
                      </Text>
                      <Text size="sm" c="dimmed">
                        Create new meal plans
                      </Text>
                    </Stack>
                  </Group>
                </Card>
              </Grid.Col>
            </Grid>
          )
        )}


        {/* Current Meal Plans */}
        {paginatedMealPlans.length > 0 && (
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <Title order={3}>Upcoming meal plans</Title>
              {/* Mobile: Icon button only */}
              <Box hiddenFrom="sm" className={styles.hideHistoryIcon}>
                <Tooltip label="View history" withArrow>
                  <Button
                    variant="subtle"
                    size="sm"
                    onClick={() => navigate('/meal-plans')}
                  >
                    <IconHistory size={16} />
                  </Button>
                </Tooltip>
              </Box>
              {/* Desktop: Text button */}
              <Box visibleFrom="sm">
                <Button 
                  variant="subtle" 
                  size="sm"
                  leftSection={<IconHistory size={16} />}
                  onClick={() => navigate('/meal-plans')}
                >
                  View history
                </Button>
              </Box>
            </Group>
            <Stack gap="sm">
              {paginatedMealPlans.map((plan) => (
                <Paper key={plan.id} p="md" radius="lg" withBorder>
                  {/* Mobile layout - vertical stack */}
                  <Box hiddenFrom="sm">
                    <Stack gap="sm">
                      <Group gap="xs" align="center" justify="space-between" wrap="nowrap">
                        <Group gap="xs" align="center" style={{ minWidth: 0 }}>
                          <Text fw={500}>
                            {formatDateRange(plan)}
                          </Text>
                          {getStatusBadge(plan)}
                        </Group>
                        <MealPlanCardMenu
                          mealPlan={plan}
                          userId={user?.uid || ''}
                          onDeletePlan={handleDeleteMealPlan}
                          onPlanUpdated={() => fetchMealPlans(false)}
                          profilePublic={user?.profilePublic}
                          calendarConnected={user?.familyProfile?.googleCalendarConnected ?? false}
                        />
                      </Group>

                      <Text size="sm" c="dimmed">
                        Created {formatCreatedDate(plan.createdAt)} • {formatRecipeSource(plan.preferences.recipeSources)}
                        {plan.generationStatus.status === 'completed' && plan.assignments && (
                          <> • {formatPlanMealsSummary(plan)}</>
                        )}
                      </Text>
                      
                      {plan.generationStatus.status === 'completed' && stripAssignments(plan).length > 0 && (
                        <Group gap="xs">
                          {stripAssignments(plan).slice(0, 3).map((assignment, index) => {
                            if (!hasAssignmentThumbnail(assignment, plan)) return null;
                            return (
                            <Box key={index} pos="relative">
                              <AssignmentThumbnail
                                assignment={assignment}
                                plan={plan}
                                radius="md"
                                onRecipeClick={(recipe) => handleRecipeClick(recipe, plan.id, assignment.id)}
                              />
                              {index === 2 && stripAssignments(plan).length > 3 && (
                                <Tooltip
                                  label={stripAssignments(plan).slice(2).map(a => assignmentThumbnailLabel(a, plan)).filter(Boolean).join(', ')}
                                  position="top" 
                                  withArrow
                                  multiline
                                  maw={300}
                                  events={{ hover: true, focus: true, touch: true }}
                                >
                                  <Box
                                    pos="absolute"
                                    top={0}
                                    left={0}
                                    right={0}
                                    bottom={0}
                                    bg="rgba(0, 0, 0, 0.6)"
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      borderRadius: 'var(--mantine-radius-md)',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <Text size="xs" c="white" fw={500}>
                                      +{stripAssignments(plan).length - 2}
                                    </Text>
                                  </Box>
                                </Tooltip>
                              )}
                            </Box>
                            );
                          })}
                        </Group>
                      )}
                      
                      {/* Stands where the three thumbnails above will land, so
                          the card keeps its shape and the week fills in. */}
                      {(plan.generationStatus.status === 'pending' || plan.generationStatus.status === 'generating') && (
                        <PlanGenerationProgress
                          variant="tiles"
                          maxTiles={3}
                          tileRadius="md"
                          progress={plan.generationProgress}
                          startedAt={plan.createdAt}
                          household={plan.profileSnapshot}
                          expectedDays={getExpectedDays(plan).map((d) => d.dayName)}
                          penciledMeals={plan.preferences.penciledMeals}
                          additionalMeals={plan.preferences.additionalMeals}
                        />
                      )}

                      {plan.generationStatus.status === 'failed' && plan.generationStatus.errorMessage && (
                        <Text size="sm" c="red">
                          {plan.generationStatus.errorMessage}
                        </Text>
                      )}

                      {plan.generationStatus.status === 'completed' ? (
                        <Button
                          size="sm"
                          variant="light"
                          onClick={() => handleViewPlan(plan)}
                          fullWidth
                        >
                          View
                        </Button>
                      ) : plan.generationStatus.status === 'pending' || plan.generationStatus.status === 'generating' ? (
                        <PlanGenerationProgress
                          variant="compact"
                          progress={plan.generationProgress}
                          startedAt={plan.createdAt}
                          household={plan.profileSnapshot}
                          expectedDays={getExpectedDays(plan).map((d) => d.dayName)}
                          penciledMeals={plan.preferences.penciledMeals}
                          additionalMeals={plan.preferences.additionalMeals}
                        />
                      ) : plan.generationStatus.status === 'failed' ? (
                        <Button
                          size="sm"
                          variant="light"
                          color="red"
                          onClick={() => handleClearFailedPlan(plan)}
                          loading={clearingPlanId === plan.id}
                          fullWidth
                        >
                          Clear
                        </Button>
                      ) : null}
                    </Stack>
                  </Box>

                  {/* Desktop layout - horizontal with thumbnails on left */}
                  <Box visibleFrom="sm">
                    <Group align="flex-start" gap="lg">
                      {/* Left: Recipe thumbnails */}
                      {plan.generationStatus.status === 'completed' || plan.generationStatus.status === 'failed' ? (
                        <Group gap="xs">
                          {stripAssignments(plan).slice(0, 7).map((assignment, index) => {
                            if (!hasAssignmentThumbnail(assignment, plan)) return null;
                            return (
                            <Box key={index} pos="relative">
                              <AssignmentThumbnail
                                assignment={assignment}
                                plan={plan}
                                radius="sm"
                                onRecipeClick={(recipe) => handleRecipeClick(recipe, plan.id, assignment.id)}
                              />
                              {index === 6 && stripAssignments(plan).length > 7 && (
                                <Tooltip
                                  label={stripAssignments(plan).slice(6).map(a => assignmentThumbnailLabel(a, plan)).filter(Boolean).join(', ')}
                                  position="top" 
                                  withArrow
                                  multiline
                                  maw={300}
                                  events={{ hover: true, focus: true, touch: true }}
                                >
                                  <Box
                                    pos="absolute"
                                    top={0}
                                    left={0}
                                    right={0}
                                    bottom={0}
                                    bg="rgba(0, 0, 0, 0.6)"
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      borderRadius: 'var(--mantine-radius-sm)',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <Text size="xs" c="white" fw={500}>
                                      +{stripAssignments(plan).length - 6}
                                    </Text>
                                  </Box>
                                </Tooltip>
                              )}
                            </Box>
                            );
                          })}
                        </Group>
                      ) : (
                        <PlanGenerationProgress
                          variant="tiles"
                          progress={plan.generationProgress}
                          startedAt={plan.createdAt}
                          household={plan.profileSnapshot}
                          expectedDays={getExpectedDays(plan).map((d) => d.dayName)}
                          penciledMeals={plan.preferences.penciledMeals}
                          additionalMeals={plan.preferences.additionalMeals}
                        />
                      )}

                      {/* Right: Meal plan details */}
                      <Stack gap="xs" style={{ flex: 1 }}>
                        {/* nowrap + shrinkable left column: a long meta line
                            must wrap within its own column, never push the
                            View/menu cluster off the top-right corner */}
                        <Group justify="space-between" align="flex-start" wrap="nowrap">
                          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                            <Group gap="sm" align="center">
                              <Text fw={500} size="lg">
                                {formatDateRange(plan)}
                              </Text>
                              {getStatusBadge(plan)}
                            </Group>
                            
                            <Text size="sm" c="dimmed">
                              Created {formatCreatedDate(plan.createdAt)} • {formatRecipeSource(plan.preferences.recipeSources)}
                              {plan.generationStatus.status === 'completed' && plan.assignments && (
                                <> • {formatPlanMealsSummary(plan)}</>
                              )}
                            </Text>
                            
                            {plan.generationStatus.status === 'failed' && plan.generationStatus.errorMessage && (
                              <Text size="sm" c="red">
                                {plan.generationStatus.errorMessage}
                              </Text>
                            )}
                          </Stack>

                          {/* Desktop action button */}
                          <Group gap="xs" wrap="nowrap" align="center">
                            {plan.generationStatus.status === 'completed' ? (
                              <Button
                                size="sm"
                                variant="light"
                                onClick={() => handleViewPlan(plan)}
                              >
                                View
                              </Button>
                            ) : plan.generationStatus.status === 'pending' || plan.generationStatus.status === 'generating' ? (
                              <Group justify="center" w={60}>
                                <Loader size="sm" />
                              </Group>
                            ) : plan.generationStatus.status === 'failed' ? (
                              <Button
                                size="sm"
                                variant="light"
                                color="red"
                                onClick={() => handleClearFailedPlan(plan)}
                                loading={clearingPlanId === plan.id}
                              >
                                Clear
                              </Button>
                            ) : null}
                            <MealPlanCardMenu
                              mealPlan={plan}
                              userId={user?.uid || ''}
                              onDeletePlan={handleDeleteMealPlan}
                              onPlanUpdated={() => fetchMealPlans(false)}
                              profilePublic={user?.profilePublic}
                              calendarConnected={user?.familyProfile?.googleCalendarConnected ?? false}
                            />
                          </Group>
                        </Group>
                      </Stack>
                    </Group>
                  </Box>
                </Paper>
              ))}
            </Stack>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <Group justify="center" mt="md">
                <Pagination
                  value={currentPage}
                  onChange={setCurrentPage}
                  total={totalPages}
                  size="sm"
                />
              </Group>
            )}
          </Stack>
        )}

        {/* Cookbook CTA */}
        {(!hasInitiallyLoaded || recipesLoading) ? (
          <Skeleton height={110} radius="xl" />
        ) : (
          cookbookCount === 0 ? (
            <EmptyCookbookCTA
              onImportRecipes={() => setImportModalOpened(true)}
            />
          ) : (
            <PopulatedCookbookCTA
              recipes={sampleRecipes}
              recipeCount={cookbookCount}
              onViewCookbook={() => navigate('/cookbook')}
            />
          )
        )}

        {/* Pantry CTA */}
        {hasInitiallyLoaded && !pantryLoading && pantryCount === 0 && (
          <EmptyPantryCTA
            onAddPantryItems={() => navigate('/pantry')}
          />
        )}

      </Stack>

      {/* Failed Meal Plans Modal */}
      <FailedMealPlansModal
        opened={failedMealPlansModalOpened}
        onClose={() => setFailedMealPlansModalOpened(false)}
        failedMealPlans={failedMealPlans}
        onMealPlansUpdated={() => {
          fetchMealPlans(false); // Refresh meal plans list
        }}
      />

      {/* Import Recipe Modal */}
      <ImportRecipeModal
        opened={importModalOpened}
        onClose={() => setImportModalOpened(false)}
        onImportStarted={() => {
          setImportModalOpened(false);
          navigate('/cookbook');
        }}
      />
    </MobileOptimized>
  );
};
